// v3 workbook exercise engine — pure generation + grading. The client builds and
// grades a randomized, non-repeating run from public reference data (word pools,
// paradigm tables, sentence templates, illustration scenes) and submits only the
// score (COURSES-SPEC §3). Determinism + variety come from a seeded PRNG, so a
// given (config, seed) reproduces a run and a fuzz test can prove no item is ever
// ambiguous. Every generated item has exactly ONE correct answer (or an authored
// accept set) and no also-correct distractor — see buildOptions / the syncretism
// index below.

import type {
  AnyExerciseSetConfig, ExerciseSetConfigV3, ExerciseStepV3, ExerciseSetConfig as ExerciseSetConfigV2,
  FeatureTuple, GrammaticalCase, GrammaticalNumber, GrammaticalPerson,
} from '@vedaunion/shared';
import { makeRng, childSeed, type Rng } from './prng';
import { paradigmSet, LEMMAS } from './paradigms';
import { poolItems, type WordItem } from './wordpool';
import { fillTemplates, identifyTemplates, clozeTemplates } from './templates';
import { scenes as sceneBank } from './scenes';
import { splitWord } from './akshara';
import { sequenceItems } from './sequences';
import { categorizeBank } from './categories';
import { translations } from './translations';
import { sandhiItems } from './sandhi';
import type {
  GeneratedExercise, GenChoice, GenType, GenMatch, GenMatchItem,
  GenOrder, GenCategorize, GenCloze, GenParse, GenParseAxis,
  ParadigmCell, ParadigmEntry, PromptModel, PromptScript, SentenceSeg,
} from './types';

// ---------------------------------------------------------------------------
// Normalization (the basis of deterministic, auditable grading)
// ---------------------------------------------------------------------------
const norm = (s: string) => s.normalize('NFC').trim();
const normIast = (s: string) => norm(s).toLowerCase();
const cmpFor = (script: PromptScript) => (script === 'iast' ? normIast : norm);

// ---------------------------------------------------------------------------
// Feature labels
// ---------------------------------------------------------------------------
const CASE_LABEL: Record<GrammaticalCase, string> = {
  nom: 'nominative', acc: 'accusative', ins: 'instrumental', dat: 'dative',
  abl: 'ablative', gen: 'genitive', loc: 'locative', voc: 'vocative',
};
const NUM_LABEL: Record<GrammaticalNumber, string> = { sg: 'singular', dual: 'dual', pl: 'plural' };
const PERSON_LABEL: Record<GrammaticalPerson, string> = { p1: 'first person', p2: 'second person', p3: 'third person' };

function featureKey(f: FeatureTuple): string {
  return `${f.case ?? f.person}.${f.number}`;
}
function featureLabel(f: FeatureTuple, axis: 'case' | 'number' | 'person' | 'case+number'): string {
  switch (axis) {
    case 'case': return f.case ? CASE_LABEL[f.case] : '';
    case 'person': return f.person ? PERSON_LABEL[f.person] : '';
    case 'number': return NUM_LABEL[f.number];
    case 'case+number': return f.case ? `${CASE_LABEL[f.case]} ${NUM_LABEL[f.number]}` : `${PERSON_LABEL[f.person!]} ${NUM_LABEL[f.number]}`;
  }
}
/** Full human cue for a paradigm slot, e.g. "accusative singular" / "3rd person plural". */
function fullFeatureLabel(f: FeatureTuple): string {
  return f.case ? `${CASE_LABEL[f.case]} ${NUM_LABEL[f.number]}` : `${PERSON_LABEL[f.person!]} ${NUM_LABEL[f.number]}`;
}

// ---------------------------------------------------------------------------
// Paradigm helpers + syncretism
// ---------------------------------------------------------------------------
function cellFor(entry: ParadigmEntry, f: FeatureTuple): ParadigmCell | undefined {
  const k = featureKey(f);
  return entry.cells.find((c) => featureKey(c.feature) === k);
}
function surface(cell: ParadigmCell, script: 'deva' | 'iast'): string {
  return script === 'deva' ? cell.deva : cell.iast;
}
function findEntry(paradigmSetKey: string, lemmaId: string): ParadigmEntry | undefined {
  return paradigmSet(paradigmSetKey).find((e) => e.lemma.id === lemmaId) ?? LEMMAS[lemmaId];
}

// ---------------------------------------------------------------------------
// Option builder — the anti-ambiguity guarantee
//
// Given the answer (value + discriminator) and candidate distractors (each with
// its own discriminator), produce a shuffled option list in which EXACTLY ONE
// option is correct: a candidate is admissible only if its discriminator differs
// from the answer's (so it is provably wrong) AND its surface value differs. If
// too few admissible distractors exist, the option count shrinks (min 2) rather
// than admit an also-correct option.
// ---------------------------------------------------------------------------
interface Cand { value: string; disc: string }
function buildOptions(rng: Rng, answer: Cand, candidates: Cand[], optionCount: number, eq: (a: string, b: string) => boolean): string[] {
  const seenVal = new Set([eqKey(answer.value, eq)]);
  const seenDisc = new Set([answer.disc]);
  const distractors: string[] = [];
  for (const c of rng.shuffle(candidates)) {
    if (distractors.length >= optionCount - 1) break;
    if (c.disc === answer.disc) continue;              // also-correct → reject
    if (seenDisc.has(c.disc)) continue;                // duplicate discriminator
    const vk = eqKey(c.value, eq);
    if (seenVal.has(vk)) continue;                     // duplicate surface
    seenDisc.add(c.disc);
    seenVal.add(vk);
    distractors.push(c.value);
  }
  return rng.shuffle([answer.value, ...distractors]);
}
function eqKey(v: string, eq: (a: string, b: string) => boolean): string {
  // Cheap canonical key consistent with eq (NFC; lowercased when eq is case-insensitive).
  return eq('A', 'a') ? normIast(v) : norm(v);
}
const eqExact = (a: string, b: string) => norm(a) === norm(b);
const eqIast = (a: string, b: string) => normIast(a) === normIast(b);

// ---------------------------------------------------------------------------
// Word-pool direction mapping (multiple-choice / type-answer / match)
// ---------------------------------------------------------------------------
type Field = 'deva' | 'iast' | 'gloss';
function fieldOf(item: WordItem, f: Field): string {
  return f === 'deva' ? item.deva : f === 'iast' ? item.iast : item.gloss;
}
const MC_MAP: Record<string, { p: Field; ps: PromptScript; a: Field; as: PromptScript }> = {
  'deva->iast': { p: 'deva', ps: 'deva', a: 'iast', as: 'iast' },
  'iast->deva': { p: 'iast', ps: 'iast', a: 'deva', as: 'deva' },
  'form->label': { p: 'deva', ps: 'deva', a: 'gloss', as: 'label' },
  'label->form': { p: 'gloss', ps: 'label', a: 'deva', as: 'deva' },
  'word->meaning': { p: 'deva', ps: 'deva', a: 'gloss', as: 'label' },
  'meaning->word': { p: 'gloss', ps: 'label', a: 'deva', as: 'deva' },
};

// ---------------------------------------------------------------------------
// Sentence segmentation (fill-blank slot / identify mark)
// ---------------------------------------------------------------------------
function splitBlank(text: string, filled?: string): SentenceSeg[] {
  const parts = text.split('{}');
  const segs: SentenceSeg[] = [];
  parts.forEach((p, i) => {
    if (p) segs.push({ text: p });
    if (i < parts.length - 1) segs.push({ text: filled ?? '', role: 'blank' });
  });
  return segs;
}
/** Split a multi-blank frame: each '{}' becomes a blank slot carrying its id (in order). */
function splitClozeBlanks(text: string, blankIds: string[]): SentenceSeg[] {
  const parts = text.split('{}');
  const segs: SentenceSeg[] = [];
  parts.forEach((p, i) => {
    if (p) segs.push({ text: p });
    if (i < parts.length - 1) segs.push({ text: '', role: 'blank', blankId: blankIds[i] });
  });
  return segs;
}
function splitMark(text: string): { segs: SentenceSeg[]; marked: string } {
  const m = text.match(/\[\[(.+?)\]\]/);
  const marked = m ? m[1]! : '';
  const segs: SentenceSeg[] = [];
  const before = text.slice(0, m ? m.index! : text.length);
  if (before) segs.push({ text: before });
  if (marked) segs.push({ text: marked, role: 'mark' });
  const after = m ? text.slice(m.index! + m[0].length) : '';
  if (after) segs.push({ text: after });
  return { segs, marked };
}

// ---------------------------------------------------------------------------
// Per-step generators
// ---------------------------------------------------------------------------
type Step = ExerciseStepV3;

function genMultipleChoice(step: Extract<Step, { type: 'multiple-choice' }>, rng: Rng): GeneratedExercise[] {
  const m = MC_MAP[step.direction]!;
  const pool = poolItems(step.poolKey, step.band);
  const picks = rng.shuffle(pool).slice(0, step.count);
  const eq = m.as === 'iast' ? eqIast : eqExact;
  return picks.map((item, i): GenChoice => {
    const answer = fieldOf(item, m.a);
    const cands: Cand[] = pool.filter((w) => w.id !== item.id).map((w) => ({ value: fieldOf(w, m.a), disc: eqKey(fieldOf(w, m.a), eq) }));
    return {
      type: 'choice',
      key: `mc-${step.bucket}-${item.id}-${i}`,
      bucket: step.bucket,
      prompt: { kind: 'token', script: m.ps, text: fieldOf(item, m.p) },
      instruction: instructionFor('multiple-choice', m.as),
      options: buildOptions(rng, { value: answer, disc: eqKey(answer, eq) }, cands, step.optionCount, eq),
      optionScript: m.as,
      answer,
      feedback: item.note ?? item.gloss,
    };
  });
}

function genTypeAnswer(step: Extract<Step, { type: 'type-answer' }>, rng: Rng): GeneratedExercise[] {
  const m = MC_MAP[step.direction]!;
  const pool = poolItems(step.poolKey, step.band);
  const picks = rng.shuffle(pool).slice(0, step.count);
  return picks.map((item, i): GenType => {
    const answer = fieldOf(item, m.a);
    return {
      type: 'type',
      key: `ty-${step.bucket}-${item.id}-${i}`,
      bucket: step.bucket,
      prompt: { kind: 'token', script: m.ps, text: fieldOf(item, m.p) },
      instruction: instructionFor('type-answer', m.as),
      answer,
      accept: [answer],
      keyboard: step.keyboard,
      answerScript: m.as === 'deva' ? 'deva' : 'iast',
      feedback: item.note ?? item.gloss,
    };
  });
}

function genFillBlank(step: Extract<Step, { type: 'fill-blank' }>, rng: Rng): GeneratedExercise[] {
  const bank = fillTemplates(step.templateSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const out: GeneratedExercise[] = [];
  for (const [i, t] of picks.entries()) {
    const entry = findEntry(t.paradigmSet, t.lemmaId);
    const cell = entry && cellFor(entry, t.feature);
    if (!entry || !cell) continue;
    const ans = surface(cell, step.script);
    const text = step.script === 'deva' ? t.deva : t.iast;
    const cue = `${entry.lemma.iast} → ${fullFeatureLabel(t.feature)}`;
    if (step.mode === 'choose') {
      const cands: Cand[] = entry.cells.map((c) => ({ value: surface(c, step.script), disc: surface(c, step.script) }));
      out.push({
        type: 'choice', key: `fb-${t.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'sentence', script: step.script, segments: splitBlank(text), cue },
        instruction: 'Choose the correct form for the blank',
        options: buildOptions(rng, { value: ans, disc: ans }, cands, step.optionCount, step.script === 'iast' ? eqIast : eqExact),
        optionScript: step.script, answer: ans, feedback: t.gloss,
      });
    } else {
      out.push({
        type: 'type', key: `fb-${t.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'sentence', script: step.script, segments: splitBlank(text), cue },
        instruction: 'Type the correct form for the blank',
        answer: ans, accept: [ans], keyboard: step.script === 'deva' ? 'devanagari' : 'iast',
        answerScript: step.script, feedback: t.gloss,
      });
    }
  }
  return out;
}

function genInflect(step: Extract<Step, { type: 'inflect' }>, rng: Rng): GeneratedExercise[] {
  const entries = paradigmSet(step.paradigmSet);
  // Build (entry, target) candidate slots.
  const slots: { entry: ParadigmEntry; cell: ParadigmCell }[] = [];
  for (const entry of entries) {
    const targets = step.targets.length
      ? entry.cells.filter((c) => step.targets.some((t) => featureKey(t) === featureKey(c.feature)))
      : entry.cells;
    for (const cell of targets) slots.push({ entry, cell });
  }
  const picks = rng.shuffle(slots).slice(0, step.count);
  return picks.map(({ entry, cell }, i): GeneratedExercise => {
    const ans = surface(cell, step.script);
    const prompt: PromptModel = {
      kind: 'token', script: step.script === 'deva' ? 'deva' : 'iast',
      text: step.script === 'deva' ? entry.lemma.deva : entry.lemma.iast,
      sub: `${entry.lemma.gloss} · ${fullFeatureLabel(cell.feature)}`,
    };
    if (step.mode === 'choose') {
      const cands: Cand[] = entry.cells.map((c) => ({ value: surface(c, step.script), disc: surface(c, step.script) }));
      return {
        type: 'choice', key: `inf-${entry.lemma.id}-${featureKey(cell.feature)}-${i}`, bucket: step.bucket,
        prompt, instruction: 'Choose the correct form',
        options: buildOptions(rng, { value: ans, disc: ans }, cands, step.optionCount, step.script === 'iast' ? eqIast : eqExact),
        optionScript: step.script, answer: ans, feedback: `${entry.lemma.iast} — ${fullFeatureLabel(cell.feature)}`,
      };
    }
    return {
      type: 'type', key: `inf-${entry.lemma.id}-${featureKey(cell.feature)}-${i}`, bucket: step.bucket,
      prompt, instruction: 'Type the correct form',
      answer: ans, accept: [ans], keyboard: step.script === 'deva' ? 'devanagari' : 'iast',
      answerScript: step.script, feedback: `${entry.lemma.iast} — ${fullFeatureLabel(cell.feature)}`,
    };
  });
}

function genMatch(step: Extract<Step, { type: 'match' }>, rng: Rng): GeneratedExercise[] {
  const pool = poolItems(step.poolKey, step.band);
  const fields: Record<string, { l: Field; ls: PromptScript; r: Field; rs: PromptScript }> = {
    'deva-iast': { l: 'deva', ls: 'deva', r: 'iast', rs: 'iast' },
    'word-meaning': { l: 'deva', ls: 'deva', r: 'gloss', rs: 'label' },
    'form-label': { l: 'deva', ls: 'deva', r: 'gloss', rs: 'label' },
  };
  const map = fields[step.pairKind]!;
  // How many match exercises: each consumes pairCount items; produce `count` of them.
  const out: GeneratedExercise[] = [];
  const used = new Set<string>();
  for (let n = 0; n < step.count; n++) {
    const avail = pool.filter((w) => !used.has(w.id));
    const chosen: WordItem[] = [];
    const seenL = new Set<string>();
    const seenR = new Set<string>();
    for (const w of rng.shuffle(avail)) {
      if (chosen.length >= step.pairCount) break;
      const lv = norm(fieldOf(w, map.l));
      const rv = norm(fieldOf(w, map.r));
      if (seenL.has(lv) || seenR.has(rv)) continue; // both columns must be distinct
      seenL.add(lv); seenR.add(rv); chosen.push(w);
    }
    if (chosen.length < 3) break; // not enough for a sound match
    chosen.forEach((w) => used.add(w.id));
    const left: GenMatchItem[] = rng.shuffle(chosen).map((w) => ({ id: w.id, text: fieldOf(w, map.l), script: map.ls }));
    const right: GenMatchItem[] = rng.shuffle(chosen).map((w) => ({ id: w.id, text: fieldOf(w, map.r), script: map.rs }));
    const correct: Record<string, string> = {};
    chosen.forEach((w) => { correct[w.id] = w.id; });
    out.push({
      type: 'match', key: `mt-${step.bucket}-${n}`, bucket: step.bucket,
      prompt: { kind: 'token', script: 'label', text: '' },
      instruction: 'Match each item on the left to its pair on the right',
      left, right, correct, feedback: '',
    });
  }
  return out;
}

function genIdentify(step: Extract<Step, { type: 'identify' }>, rng: Rng): GeneratedExercise[] {
  const bank = identifyTemplates(step.templateSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const axis = step.axis;
  // Label option bank per axis.
  const labelBank = (f: FeatureTuple): string[] => {
    if (axis === 'case') return (Object.keys(CASE_LABEL) as GrammaticalCase[]).map((c) => CASE_LABEL[c]);
    if (axis === 'number') return (Object.keys(NUM_LABEL) as GrammaticalNumber[]).map((n) => NUM_LABEL[n]);
    if (axis === 'person') return (Object.keys(PERSON_LABEL) as GrammaticalPerson[]).map((p) => PERSON_LABEL[p]);
    // case+number — build from the form's own number against all cases
    return (Object.keys(CASE_LABEL) as GrammaticalCase[]).map((c) => `${CASE_LABEL[c]} ${NUM_LABEL[f.number]}`);
  };
  const out: GeneratedExercise[] = [];
  for (const [i, t] of picks.entries()) {
    // Verify the marked surface matches the paradigm (skip if authoring drifted).
    const entry = findEntry(t.paradigmSet, t.lemmaId);
    const marked = splitMark(t.iast).marked;
    const devaSplit = splitMark(t.deva);
    if (!entry) continue;
    const cell = cellFor(entry, t.feature);
    if (!cell || (normIast(cell.iast) !== normIast(marked) && norm(cell.deva) !== norm(devaSplit.marked))) continue;
    const ansLabel = featureLabel(t.feature, axis);
    const cands: Cand[] = labelBank(t.feature).filter((l) => l !== ansLabel).map((l) => ({ value: l, disc: l }));
    out.push({
      type: 'choice', key: `id-${t.id}-${i}`, bucket: step.bucket,
      // Show the Devanāgarī sentence with the marked word highlighted.
      prompt: { kind: 'sentence', script: 'deva', segments: devaSplit.segs, cue: t.iast.replace(/\[\[(.+?)\]\]/, '$1') },
      instruction: `Identify the ${axis === 'case+number' ? 'case and number' : axis} of the highlighted word`,
      options: buildOptions(rng, { value: ansLabel, disc: ansLabel }, cands, step.optionCount, eqExact),
      optionScript: 'label', answer: ansLabel, feedback: `${marked} — ${fullFeatureLabel(t.feature)}. ${t.gloss}`,
    });
  }
  return out;
}

function genIllustration(step: Extract<Step, { type: 'illustration' }>, rng: Rng): GeneratedExercise[] {
  const bank = sceneBank(step.sceneSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const out: GeneratedExercise[] = [];
  for (const [i, sc] of picks.entries()) {
    const entry = findEntry(sc.paradigmSet, sc.lemmaId);
    const cell = entry && cellFor(entry, sc.feature);
    if (!entry || !cell) continue;
    const ans = surface(cell, step.script);
    const prompt: PromptModel = { kind: 'illustration', svg: sc.svg, instruction: sc.instruction, caption: entry.lemma.gloss };
    if (step.mode === 'type') {
      out.push({
        type: 'type', key: `il-${sc.id}-${i}`, bucket: step.bucket, prompt,
        instruction: sc.instruction, answer: ans, accept: [ans],
        keyboard: step.script === 'deva' ? 'devanagari' : 'iast', answerScript: step.script, feedback: sc.gloss,
      });
    } else {
      const cands: Cand[] = entry.cells.map((c) => ({ value: surface(c, step.script), disc: surface(c, step.script) }));
      out.push({
        type: 'choice', key: `il-${sc.id}-${i}`, bucket: step.bucket, prompt,
        instruction: sc.instruction,
        options: buildOptions(rng, { value: ans, disc: ans }, cands, step.optionCount, step.script === 'iast' ? eqIast : eqExact),
        optionScript: step.script, answer: ans, feedback: sc.gloss,
      });
    }
  }
  return out;
}

// ---- order: assemble shuffled tiles into one correct sequence --------------
function genOrder(step: Extract<Step, { type: 'order' }>, rng: Rng): GeneratedExercise[] {
  const out: GeneratedExercise[] = [];
  if (step.mode === 'akshara') {
    const pool = poolItems(step.poolKey ?? 'devanagari', step.band);
    const candidates = pool.filter((w) => {
      const t = splitWord(w.deva);
      return t !== null && t.length >= 2 && t.length <= 8;
    });
    const picks = rng.shuffle(candidates).slice(0, step.count);
    for (const [i, w] of picks.entries()) {
      const split = splitWord(w.deva)!;
      const tiles = rng.shuffle(split.map((t, k) => ({ id: `${w.id}-${k}`, text: t, script: 'deva' as PromptScript })));
      out.push({
        type: 'order', key: `or-${step.bucket}-${w.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'token', script: 'iast', text: w.iast, sub: w.gloss },
        instruction: 'Arrange the syllables to spell this word',
        tiles, answer: norm(w.deva), answerScript: 'deva', joiner: '',
        feedback: `${w.deva} — ${w.gloss}`,
      });
    }
    return out;
  }
  const bank = sequenceItems(step.sequenceSet ?? '');
  const picks = rng.shuffle(bank).slice(0, step.count);
  const useDeva = step.script === 'deva';
  for (const [i, sq] of picks.entries()) {
    const texts = sq.tiles.map((t) => (useDeva ? t.deva : t.iast));
    const tiles = rng.shuffle(sq.tiles.map((t, k) => ({ id: `${sq.id}-${k}`, text: useDeva ? t.deva : t.iast, script: (useDeva ? 'deva' : 'iast') as PromptScript })));
    out.push({
      type: 'order', key: `or-${sq.id}-${i}`, bucket: step.bucket,
      prompt: { kind: 'token', script: 'label', text: '' },
      instruction: sq.instruction,
      tiles, answer: texts.map(norm).join(' '), answerScript: useDeva ? 'deva' : 'iast', joiner: ' ',
      feedback: sq.gloss,
    });
  }
  return out;
}

// ---- categorize: sort items into labelled buckets --------------------------
function genCategorize(step: Extract<Step, { type: 'categorize' }>, rng: Rng): GeneratedExercise[] {
  const bank = categorizeBank(step.categorizeSet);
  if (!bank) return [];
  const out: GeneratedExercise[] = [];
  const field = (it: { deva: string; iast: string; label?: string }) =>
    step.script === 'iast' ? it.iast : step.script === 'label' ? (it.label ?? it.iast) : it.deva;
  for (let n = 0; n < step.count; n++) {
    let chosen = rng.shuffle(bank.items).slice(0, step.itemCount);
    const present = new Set(chosen.map((it) => it.bucketId));
    if (present.size < 2 && bank.items.length > chosen.length) {
      const other = bank.items.find((it) => !present.has(it.bucketId));
      if (other) chosen = [...chosen.slice(0, -1), other];
    }
    const items = rng.shuffle(chosen).map((it) => ({ id: it.id, text: field(it), script: step.script as PromptScript, bucketId: it.bucketId }));
    out.push({
      type: 'categorize', key: `cat-${step.bucket}-${n}`, bucket: step.bucket,
      prompt: { kind: 'token', script: 'label', text: '' },
      instruction: bank.buckets.length === 2 ? 'Sort each item into its group' : 'Sort each item into its category',
      buckets: bank.buckets.map((b) => ({ id: b.id, label: b.label })),
      items, feedback: '',
    });
  }
  return out;
}

// ---- cloze: fill several cued blanks from a shared word bank ----------------
function genCloze(step: Extract<Step, { type: 'cloze' }>, rng: Rng): GeneratedExercise[] {
  const bank = clozeTemplates(step.clozeSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const cmp = step.script === 'iast' ? normIast : norm;
  const out: GeneratedExercise[] = [];
  for (const [i, t] of picks.entries()) {
    const surfaces: string[] = [];
    const blanks: { id: string; cue: string }[] = [];
    let ok = true;
    for (const [bi, b] of t.blanks.entries()) {
      const entry = findEntry(b.paradigmSet, b.lemmaId);
      const cell = entry && cellFor(entry, b.feature);
      if (!entry || !cell) { ok = false; break; }
      surfaces.push(surface(cell, step.script));
      blanks.push({ id: `b${bi}`, cue: `${entry.lemma.iast} → ${fullFeatureLabel(b.feature)}` });
    }
    if (!ok) continue;
    // Correct fillers must be pairwise distinct (so blanks are not swappable).
    if (new Set(surfaces.map(cmp)).size !== surfaces.length) continue;
    const segments = splitClozeBlanks(step.script === 'deva' ? t.deva : t.iast, blanks.map((b) => b.id));
    const chips: { id: string; text: string }[] = [];
    const seen = new Set<string>();
    surfaces.forEach((s, k) => { const key = cmp(s); if (!seen.has(key)) { seen.add(key); chips.push({ id: `f${k}`, text: s }); } });
    const dist = (t.distractors ?? []).map((d) => (step.script === 'deva' ? d.deva : d.iast));
    for (const d of rng.shuffle(dist)) {
      if (chips.length >= surfaces.length + step.distractorCount) break;
      const key = cmp(d);
      if (seen.has(key)) continue;
      seen.add(key); chips.push({ id: `d${chips.length}`, text: d });
    }
    const correct: Record<string, string> = {};
    blanks.forEach((b, k) => {
      const chip = chips.find((c) => cmp(c.text) === cmp(surfaces[k]!));
      if (chip) correct[b.id] = chip.id;
    });
    out.push({
      type: 'cloze', key: `cz-${t.id}-${i}`, bucket: step.bucket, script: step.script,
      prompt: { kind: 'token', script: 'label', text: '' },
      instruction: 'Fill each blank from the word bank — use the cue under each blank',
      segments, blanks,
      bank: rng.shuffle(chips).map((c) => ({ id: c.id, text: c.text, script: step.script as PromptScript })),
      correct, feedback: t.gloss,
    });
  }
  return out;
}

// ---- parse: name a marked word's lemma, case/person, and number ------------
function genParse(step: Extract<Step, { type: 'parse' }>, rng: Rng): GeneratedExercise[] {
  const bank = identifyTemplates(step.templateSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const out: GeneratedExercise[] = [];
  for (const [i, t] of picks.entries()) {
    const entry = findEntry(t.paradigmSet, t.lemmaId);
    if (!entry) continue;
    const cell = cellFor(entry, t.feature);
    const devaSplit = splitMark(t.deva);
    const marked = splitMark(t.iast).marked;
    if (!cell || (normIast(cell.iast) !== normIast(marked) && norm(cell.deva) !== norm(devaSplit.marked))) continue;
    const isVerb = !!t.feature.person;
    // Lemma distractors are drawn ONLY from the stems that appear in this bank,
    // so a per-lesson checkpoint never offers an untaught stem as an option.
    const bankLemmaIds = [...new Set(bank.map((b) => b.lemmaId))];
    const axes: GenParseAxis[] = [];
    for (const axisKey of step.axes) {
      if (axisKey === 'lemma') {
        const others = bankLemmaIds.filter((id) => id !== entry.lemma.id);
        if (others.length === 0) continue; // not enough stems to make a real choice → skip the axis
        const ansLabel = `${entry.lemma.deva} (${entry.lemma.gloss})`;
        const cands: Cand[] = others
          .map((id) => findEntry(t.paradigmSet, id) ?? LEMMAS[id])
          .filter((e): e is ParadigmEntry => !!e)
          .map((e) => ({ value: `${e.lemma.deva} (${e.lemma.gloss})`, disc: e.lemma.id }));
        axes.push({ key: 'lemma', label: 'Stem (lemma)', options: buildOptions(rng, { value: ansLabel, disc: entry.lemma.id }, cands, step.optionCount, eqExact), answer: ansLabel });
      } else if (axisKey === 'feature') {
        if (isVerb) {
          const ans = PERSON_LABEL[t.feature.person!];
          const cands: Cand[] = (Object.keys(PERSON_LABEL) as GrammaticalPerson[]).filter((p) => p !== t.feature.person).map((p) => ({ value: PERSON_LABEL[p], disc: p }));
          axes.push({ key: 'feature', label: 'Person', options: buildOptions(rng, { value: ans, disc: t.feature.person! }, cands, Math.min(step.optionCount, 3), eqExact), answer: ans });
        } else {
          const ans = CASE_LABEL[t.feature.case!];
          const cands: Cand[] = (Object.keys(CASE_LABEL) as GrammaticalCase[]).filter((c) => c !== t.feature.case).map((c) => ({ value: CASE_LABEL[c], disc: c }));
          axes.push({ key: 'feature', label: 'Case', options: buildOptions(rng, { value: ans, disc: t.feature.case! }, cands, step.optionCount, eqExact), answer: ans });
        }
      } else {
        const ans = NUM_LABEL[t.feature.number];
        const cands: Cand[] = (Object.keys(NUM_LABEL) as GrammaticalNumber[]).filter((nn) => nn !== t.feature.number).map((nn) => ({ value: NUM_LABEL[nn], disc: nn }));
        axes.push({ key: 'number', label: 'Number', options: buildOptions(rng, { value: ans, disc: t.feature.number }, cands, 3, eqExact), answer: ans });
      }
    }
    out.push({
      type: 'parse', key: `pa-${t.id}-${i}`, bucket: step.bucket, script: 'deva',
      prompt: { kind: 'token', script: 'label', text: '' },
      instruction: 'Parse the highlighted word — choose its stem, case/person, and number',
      segments: devaSplit.segs, axes,
      feedback: `${marked} — ${entry.lemma.iast}, ${fullFeatureLabel(t.feature)}. ${t.gloss}`,
    });
  }
  return out;
}

// ---- translate: choose the rendering of a short sentence -------------------
function genTranslate(step: Extract<Step, { type: 'translate' }>, rng: Rng): GeneratedExercise[] {
  const bank = translations(step.translateSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  return picks.map((t, i): GenChoice => {
    if (step.direction === 'sa->en') {
      const cands: Cand[] = bank.filter((x) => x.id !== t.id).map((x) => ({ value: x.en, disc: x.id }));
      return {
        type: 'choice', key: `tl-${t.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'token', script: step.script === 'deva' ? 'deva' : 'iast', text: step.script === 'deva' ? t.deva : t.iast },
        instruction: 'Choose the correct English translation',
        options: buildOptions(rng, { value: t.en, disc: t.id }, cands, step.optionCount, eqExact),
        optionScript: 'label', answer: t.en, feedback: `${t.iast} — ${t.en}`,
      };
    }
    const answer = step.script === 'deva' ? t.deva : t.iast;
    const cands: Cand[] = bank.filter((x) => x.id !== t.id).map((x) => ({ value: step.script === 'deva' ? x.deva : x.iast, disc: x.id }));
    return {
      type: 'choice', key: `tl-${t.id}-${i}`, bucket: step.bucket,
      prompt: { kind: 'token', script: 'label', text: t.en },
      instruction: step.script === 'deva' ? 'Choose the Sanskrit (Devanāgarī) that means this' : 'Choose the Sanskrit (IAST) that means this',
      options: buildOptions(rng, { value: answer, disc: t.id }, cands, step.optionCount, step.script === 'deva' ? eqExact : eqIast),
      optionScript: step.script === 'deva' ? 'deva' : 'iast', answer, feedback: `${t.iast} — ${t.en}`,
    };
  });
}

// ---- sandhi: combine two words (the join is rule-deterministic) ------------
function genSandhi(step: Extract<Step, { type: 'sandhi' }>, rng: Rng): GeneratedExercise[] {
  const bank = sandhiItems(step.sandhiSet);
  const picks = rng.shuffle(bank).slice(0, step.count);
  const out: GeneratedExercise[] = [];
  for (const [i, s] of picks.entries()) {
    const aTxt = step.script === 'deva' ? s.a.deva : s.a.iast;
    const bTxt = step.script === 'deva' ? s.b.deva : s.b.iast;
    const ans = step.script === 'deva' ? s.joined.deva : s.joined.iast;
    const promptText = `${aTxt} + ${bTxt}`;
    const pscript: PromptScript = step.script === 'deva' ? 'deva' : 'iast';
    if (step.mode === 'type') {
      out.push({
        type: 'type', key: `sa-${s.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'token', script: pscript, text: promptText, sub: s.rule },
        instruction: 'Combine the two words by sandhi — type the result',
        answer: ans, accept: [ans], keyboard: step.script === 'deva' ? 'devanagari' : 'iast',
        answerScript: step.script === 'deva' ? 'deva' : 'iast', feedback: s.gloss,
      });
    } else {
      const cmp = step.script === 'deva' ? eqExact : eqIast;
      const cands: Cand[] = bank.filter((x) => x.id !== s.id).map((x) => ({ value: step.script === 'deva' ? x.joined.deva : x.joined.iast, disc: x.id }));
      out.push({
        type: 'choice', key: `sa-${s.id}-${i}`, bucket: step.bucket,
        prompt: { kind: 'token', script: pscript, text: promptText, sub: s.rule },
        instruction: 'Choose the correct sandhi combination',
        options: buildOptions(rng, { value: ans, disc: s.id }, cands, step.optionCount, cmp),
        optionScript: pscript, answer: ans, feedback: s.gloss,
      });
    }
  }
  return out;
}

function instructionFor(type: 'multiple-choice' | 'type-answer', answerScript: PromptScript): string {
  if (answerScript === 'iast') return type === 'type-answer' ? 'Type the transliteration (IAST)' : 'Choose the transliteration (IAST)';
  if (answerScript === 'deva') return type === 'type-answer' ? 'Type the Devanāgarī' : 'Choose the Devanāgarī';
  return type === 'type-answer' ? 'Type the answer' : 'Choose the correct answer';
}

// ---------------------------------------------------------------------------
// v2 → v3 adapter (any stray legacy quiz still runs)
// ---------------------------------------------------------------------------
function adaptV2(cfg: ExerciseSetConfigV2): ExerciseSetConfigV3 {
  const dir: Record<string, { t: 'multiple-choice' | 'type-answer'; direction: string; keyboard?: 'iast' | 'devanagari' }> = {
    'pick-iast': { t: 'multiple-choice', direction: 'deva->iast' },
    'pick-devanagari': { t: 'multiple-choice', direction: 'iast->deva' },
    'pick-gloss': { t: 'multiple-choice', direction: 'form->label' },
    'pick-form': { t: 'multiple-choice', direction: 'label->form' },
    'type-iast': { t: 'type-answer', direction: 'deva->iast', keyboard: 'iast' },
    'type-devanagari': { t: 'type-answer', direction: 'iast->deva', keyboard: 'devanagari' },
  };
  const steps = cfg.steps.map((s): ExerciseStepV3 => {
    const d = dir[s.type]!;
    if (d.t === 'type-answer') {
      return { type: 'type-answer', count: s.count, bucket: cfg.poolKey, poolKey: cfg.poolKey, band: s.band, direction: d.direction as 'deva->iast' | 'iast->deva', keyboard: d.keyboard! };
    }
    return { type: 'multiple-choice', count: s.count, bucket: cfg.poolKey, poolKey: cfg.poolKey, band: s.band, direction: d.direction as never, optionCount: 4 };
  });
  return { version: 3, kind: 'exercise-set', steps };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function generateExercises(config: AnyExerciseSetConfig, seed: number): GeneratedExercise[] {
  const cfg: ExerciseSetConfigV3 = config.version === 3 ? config : adaptV2(config);
  const out: GeneratedExercise[] = [];
  cfg.steps.forEach((step, idx) => {
    const rng = makeRng(childSeed(seed, idx));
    switch (step.type) {
      case 'multiple-choice': out.push(...genMultipleChoice(step, rng)); break;
      case 'type-answer': out.push(...genTypeAnswer(step, rng)); break;
      case 'fill-blank': out.push(...genFillBlank(step, rng)); break;
      case 'inflect': out.push(...genInflect(step, rng)); break;
      case 'match': out.push(...genMatch(step, rng)); break;
      case 'identify': out.push(...genIdentify(step, rng)); break;
      case 'illustration': out.push(...genIllustration(step, rng)); break;
      case 'order': out.push(...genOrder(step, rng)); break;
      case 'categorize': out.push(...genCategorize(step, rng)); break;
      case 'cloze': out.push(...genCloze(step, rng)); break;
      case 'parse': out.push(...genParse(step, rng)); break;
      case 'translate': out.push(...genTranslate(step, rng)); break;
      case 'sandhi': out.push(...genSandhi(step, rng)); break;
    }
  });
  return out;
}

/** Grade a single-select or type-in answer. */
export function gradeAnswer(ex: GeneratedExercise, given: string): boolean {
  if (ex.type === 'choice') {
    const cmp = cmpFor(ex.optionScript);
    return cmp(given) === cmp(ex.answer);
  }
  if (ex.type === 'type') {
    const cmp = ex.answerScript === 'iast' ? normIast : norm;
    return ex.accept.some((a) => cmp(a) === cmp(given));
  }
  return false;
}

/** Grade a match exercise: returns correct/total pairs. */
export function gradeMatch(ex: GenMatch, mapping: Record<string, string>): { correct: number; total: number } {
  const total = ex.left.length;
  let correct = 0;
  for (const l of ex.left) if (mapping[l.id] === ex.correct[l.id]) correct++;
  return { correct, total };
}

/** Grade an order exercise: the chosen tile-id sequence must rebuild the answer. */
export function gradeOrder(ex: GenOrder, order: string[]): boolean {
  const byId = new Map(ex.tiles.map((t) => [t.id, t.text]));
  const joined = order.map((id) => byId.get(id) ?? '').join(ex.joiner);
  const cmp = ex.answerScript === 'iast' ? normIast : norm;
  return cmp(joined) === cmp(ex.answer);
}

/** Grade a categorize exercise: each item dropped into its one correct bucket. */
export function gradeCategorize(ex: GenCategorize, mapping: Record<string, string>): { correct: number; total: number } {
  let correct = 0;
  for (const it of ex.items) if (mapping[it.id] === it.bucketId) correct++;
  return { correct, total: ex.items.length };
}

/** Grade a cloze exercise: each blank filled with its one correct bank chip. */
export function gradeCloze(ex: GenCloze, mapping: Record<string, string>): { correct: number; total: number } {
  let correct = 0;
  for (const b of ex.blanks) if (mapping[b.id] === ex.correct[b.id]) correct++;
  return { correct, total: ex.blanks.length };
}

/** Grade a parse exercise: count axes answered correctly. */
export function gradeParse(ex: GenParse, mapping: Record<string, string>): { correct: number; total: number } {
  let correct = 0;
  for (const ax of ex.axes) if (mapping[ax.key] === ax.answer) correct++;
  return { correct, total: ex.axes.length };
}

/** Number of "questions" an exercise counts for when scoring (multi-part = sub-items). */
export function exerciseWeight(ex: GeneratedExercise): number {
  switch (ex.type) {
    case 'match': return ex.left.length;
    case 'categorize': return ex.items.length;
    case 'cloze': return ex.blanks.length;
    case 'parse': return ex.axes.length;
    default: return 1; // choice, type, order
  }
}

export function scorePercent(correct: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((correct / total) * 100);
}
